import type {
  ConversationListResponse,
  CreateUploadEndpointV1ConversationsConversationIdUploadsPostApiArg,
  CreateUploadEndpointV1ConversationsConversationIdUploadsPostApiResponse,
} from '~/api/hermes/typedefs.autogen';
import type { WithRtkOpts } from '~/redux/api/types';

import { endpoints as autogenEnhancements } from '~/api/hermes/enhancements.autogen';
import { Tags } from '~/api/hermes/tags.autogen';

import { hermesApiRoot } from './root';

hermesApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });

type ListConversationsInfiniteArg = {
  workflowId: string;
  limit?: number;
};

type CreateUploadApiArg = Omit<
  CreateUploadEndpointV1ConversationsConversationIdUploadsPostApiArg,
  'bodyCreateUploadEndpointV1ConversationsConversationIdUploadsPost'
> & {
  bodyCreateUploadEndpointV1ConversationsConversationIdUploadsPost: {
    file: Blob;
  };
};

/**
 * @lintignore Not imported by name anywhere, but this export must exist: dropping it
 * (or knip's fix un-exporting/deleting it) risks the module never being evaluated, which
 * would mean `injectEndpoints` never registers these endpoints on the shared root api.
 */
export const hermesApi = hermesApiRoot.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    // Cursor-paginated history for one workflow. RTK owns the page list,
    // loading/error flags, and cache; `getNextPageParam` reads the opaque
    // `next_cursor`, so the consumer never tracks a cursor by hand.
    listConversationsInfinite: build.infiniteQuery<
      ConversationListResponse,
      WithRtkOpts<ListConversationsInfiniteArg>,
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
      },
      query: ({ queryArg, pageParam }) => {
        const { workflowId, limit, rtkQueryOptions } = queryArg;
        return {
          url: '/v1/conversations',
          params: {
            limit,
            cursor: pageParam ?? undefined,
            workflow_id: workflowId,
          },
          rtkQueryOptions,
        };
      },
      providesTags: [{ type: Tags.conversations, id: 'LIST' }],
    }),
    createUploadEndpointV1ConversationsConversationIdUploadsPost:
      build.mutation<
        CreateUploadEndpointV1ConversationsConversationIdUploadsPostApiResponse,
        WithRtkOpts<CreateUploadApiArg>
      >({
        query: ({
          conversationId,
          bodyCreateUploadEndpointV1ConversationsConversationIdUploadsPost,
          rtkQueryOptions,
        }) => {
          const formData = new FormData();
          formData.append(
            'file',
            bodyCreateUploadEndpointV1ConversationsConversationIdUploadsPost.file,
          );

          return {
            url: `/v1/conversations/${conversationId}/uploads`,
            method: 'POST',
            body: formData,
            rtkQueryOptions,
          };
        },
      }),
  }),
});
